import { useState, useEffect, useMemo } from "react";
import { todayISO } from "@/lib/utils";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import {
  feedInventoryReportService,
  type FeedInventoryReport,
} from "@/services/feedInventoryReportService";
import { handleApiError } from "@/services/api";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

const fmtQtyRaw = (v: number, unit?: string) => {
  if (v === 0) return "-";
  const val = unit === "ton" || unit === "tonne" ? v / 1000 : v;
  return val.toLocaleString(undefined, { maximumFractionDigits: 3 });
};
const fmtDate = (d: string) => {
  const dt = new Date(d);
  return `${dt.getDate()}/${dt.getMonth() + 1}`;
};

export default function FeedInventoryReportPage() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState(todayISO());
  const [report, setReport] = useState<FeedInventoryReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<string>("__all__");

  useEffect(() => {
    setLoading(true);
    feedInventoryReportService
      .getReport(from || undefined, to || undefined)
      .then((data) => {
        setReport(data);
        const mats = data.materials ?? [];
        if (mats.length > 0 && selectedMaterial === "__all__") {
          setSelectedMaterial([...mats].sort()[0]);
        }
      })
      .catch(handleApiError)
      .finally(() => setLoading(false));
  }, [from, to]);

  const materials = useMemo(() => {
    if (!report) return [];
    return [...(report.materials ?? [])].sort();
  }, [report]);

  const filteredRows = useMemo(() => {
    if (!report) return [];
    const rows = report.rows ?? [];
    if (selectedMaterial === "__all__") return rows;
    return rows.filter((r) => r.raw_material_name === selectedMaterial);
  }, [report, selectedMaterial]);

  // Group rows by date for "all materials" view
  const isAllView = selectedMaterial === "__all__";

  // Summary for selected material
  const summary = useMemo(() => {
    if (!filteredRows.length) return null;
    const totalPurchases = filteredRows.reduce((s, r) => s + r.purchases, 0);
    const totalConsumption = filteredRows.reduce((s, r) => s + r.consumption, 0);
    const totalAdjustments = filteredRows.reduce((s, r) => s + r.adjustments, 0);
    // Rows arrive in DESC date order (the service reverses them so the latest
    // day is first), so [0] is the latest day and the last element is the
    // earliest. The PERIOD's opening balance is the earliest day's opening; its
    // closing balance is the latest day's closing. (These were swapped before,
    // making Opening + Purchases − Consumed ± Adj not foot to Closing.)
    const latestRow = filteredRows[0];
    const earliestRow = filteredRows[filteredRows.length - 1];
    return {
      openingStock: earliestRow.opening_stock,
      totalPurchases,
      totalConsumption,
      totalAdjustments,
      closingStock: latestRow.closing_stock,
      unit: latestRow.unit,
    };
  }, [filteredRows]);

  return (
    <>
      <PageMeta title="Feed Inventory Report | Poultry Book" description="Daily feed raw material inventory" />
      <PageBreadcrumb pageTitle="Feed Inventory" />

      <div className="card">
        {/* Filters */}
        <div className="border-b border-gray-200 p-4 dark:border-gray-700">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Label>Raw Material</Label>
              <select
                className={selectClass}
                value={selectedMaterial}
                onChange={(e) => setSelectedMaterial(e.target.value)}
              >
                <option value="__all__">All Materials</option>
                {materials.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>

          {summary && !isAllView && (
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
              <span><strong>Opening:</strong> {fmtQtyRaw(summary.openingStock, summary.unit)} {summary.unit}</span>
              <span><strong>Purchases:</strong> +{fmtQtyRaw(summary.totalPurchases, summary.unit)}</span>
              <span><strong>Consumed:</strong> -{fmtQtyRaw(summary.totalConsumption, summary.unit)}</span>
              {summary.totalAdjustments !== 0 && (
                <span><strong>Adj:</strong> {summary.totalAdjustments > 0 ? "+" : ""}{fmtQtyRaw(summary.totalAdjustments, summary.unit)}</span>
              )}
              <span><strong>Closing:</strong> {fmtQtyRaw(summary.closingStock, summary.unit)} {summary.unit}</span>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading report...</div>
          ) : filteredRows.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No data for the selected period.</div>
          ) : (
            <table className="w-max min-w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 text-left">Date</th>
                  {isAllView && (
                    <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 text-left">Material</th>
                  )}
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 text-right">Opening</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 text-right">Purchase</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 text-right">Consumed</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 text-right">Adj</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 text-right">Closing</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 text-left">Unit</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, i) => {
                  const hasActivity = row.purchases > 0 || row.consumption > 0 || row.adjustments !== 0;
                  return (
                    <tr
                      key={`${row.date}-${row.raw_material_id}`}
                      className={`border-b border-gray-50 dark:border-gray-800 ${
                        i % 2 === 0 ? "" : "bg-gray-50/50 dark:bg-gray-800/30"
                      } ${!hasActivity && !isAllView ? "opacity-50" : ""}`}
                    >
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                        {fmtDate(row.date)}
                      </td>
                      {isAllView && (
                        <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                          {row.raw_material_name}
                        </td>
                      )}
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-right text-gray-700 dark:text-gray-300">
                        {fmtQtyRaw(row.opening_stock, row.unit)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-right text-success-700 dark:text-success-400 font-medium">
                        {fmtQtyRaw(row.purchases, row.unit)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-right text-error-600 dark:text-error-400 font-medium">
                        {row.consumption > 0 ? fmtQtyRaw(row.consumption, row.unit) : "-"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-right text-gray-600 dark:text-gray-400">
                        {row.adjustments !== 0 ? (row.adjustments > 0 ? "+" : "") + fmtQtyRaw(row.adjustments, row.unit) : "-"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-right font-semibold text-gray-700 dark:text-gray-300">
                        {fmtQtyRaw(row.closing_stock, row.unit)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                        {row.unit}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
